import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { APP_NAME } from '@/lib/brand'

interface ForgotForm {
  email: string
}

export function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotForm>()

  async function onSubmit(data: ForgotForm) {
    setApiError(null)
    try {
      await api.post('/auth/forgot-password', data)
      setSubmitted(true)
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data?.error?.message as string | undefined) : undefined
      setApiError(msg ?? 'Erro inesperado.')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{APP_NAME}</h1>
          <p className="text-sm text-muted-foreground">Recuperação de senha</p>
        </div>

        {submitted ? (
          <div className="space-y-3 rounded-lg border border-border bg-card p-4 text-sm text-foreground">
            <p>Se o email existir em nossa base, enviamos sua solicitação para o suporte.</p>
            <p className="text-muted-foreground">
              Em breve, um operador entrará em contato com o link de redefinição de senha.
            </p>
            <Link
              to="/login"
              className="inline-block text-sm font-medium text-foreground underline-offset-4 hover:underline"
            >
              Voltar ao login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Informe seu email cadastrado. Você receberá um link para redefinir a senha por meio do suporte.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email', {
                  required: 'Informe seu email',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
                })}
              />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>

            {apiError && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {apiError}
              </p>
            )}

            <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando…' : 'Solicitar reset'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="font-medium text-foreground underline-offset-4 hover:underline">
                Voltar ao login
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
